import React, { useEffect, useMemo, useRef, useState } from "react";
import { m, useReducedMotion } from "framer-motion";
import { Image as ImageIcon, RefreshCw } from "lucide-react";
import {
  getMobileMarketingAsset,
  resolveApprovedAssetSources,
} from "./mobileMarketingAssets";

function Placeholder({ state, label, onRetry, reducedMotion, announce }) {
  if (state === "error") {
    return (
      <div className="absolute inset-0 grid place-items-center p-6 text-center" role={announce ? "alert" : undefined} aria-hidden={announce ? undefined : "true"}>
        <div>
          <ImageIcon className="mx-auto h-7 w-7 text-ink-subtle" aria-hidden="true" />
          <p className="mt-3 text-sm font-semibold text-ink">Visual unavailable</p>
          <p className="mt-1 text-xs leading-relaxed text-ink-subtle">Everything else on this page has loaded — only the image is missing.</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-line bg-surface px-4 text-sm font-semibold text-ink transition-transform duration-200 hover:border-orange-border active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/50"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Retry image
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 grid place-items-center p-6 text-center" role={announce ? "status" : undefined} aria-live={announce ? "polite" : undefined} aria-hidden={announce ? undefined : "true"}>
      <div>
        <m.div
          aria-hidden="true"
          animate={reducedMotion || state !== "loading" ? undefined : { opacity: [0.62, 1, 0.62], scale: [0.96, 1, 0.96] }}
          transition={reducedMotion || state !== "loading" ? undefined : { duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ImageIcon className="mx-auto h-7 w-7 text-orange-text" />
        </m.div>
        <p className="mt-3 text-sm font-semibold text-ink">
          {state === "loading" ? "Loading approved visual" : label}
        </p>
        <p className="mt-1 text-xs text-ink-subtle">
          {state === "loading" ? "Just a moment." : "This visual is not available on the public site."}
        </p>
      </div>
    </div>
  );
}

export function MarketingAssetSlot({
  assetKey,
  asset: assetOverride,
  alt,
  className = "",
  fit = "contain",
  priority = false,
  announce = true,
  sizes = "(min-width: 1024px) 50vw, 100vw",
  pendingLabel = "Visual held for review",
}) {
  const reducedMotion = useReducedMotion();
  const asset = assetOverride || getMobileMarketingAsset(assetKey);
  const sources = useMemo(() => resolveApprovedAssetSources(asset), [asset]);
  const [attempt, setAttempt] = useState(0);
  const loadKey = `${asset?.key || assetKey}:${sources?.fallbackSrc || "pending"}:${attempt}`;
  const mountedRef = useRef(true);
  const currentLoadKeyRef = useRef(loadKey);
  currentLoadKeyRef.current = loadKey;
  const [loadState, setLoadState] = useState(() => ({
    key: loadKey,
    value: sources ? "loading" : "pending",
  }));
  // Derive the new request state during render. An effect-based reset can run after a cached
  // image's load event and incorrectly hide an already-complete image behind the placeholder.
  const state = loadState.key === loadKey
    ? loadState.value
    : (sources ? "loading" : "pending");

  const width = Number(asset?.width) || 4;
  const height = Number(asset?.height) || 3;
  const resolvedAlt = alt === undefined ? (asset?.alt || "") : alt;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const markImageReadyAfterDecode = (event) => {
    const image = event.currentTarget;
    const markReady = () => {
      if (!mountedRef.current || currentLoadKeyRef.current !== loadKey || !image.isConnected) return;
      setLoadState({ key: loadKey, value: "ready" });
    };

    if (typeof image.decode !== "function") {
      markReady();
      return;
    }

    let decodeResult;
    try {
      decodeResult = image.decode();
    } catch (_error) {
      markReady();
      return;
    }
    Promise.resolve(decodeResult).catch(() => undefined).then(markReady);
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-line bg-muted ${className}`}
      style={{ aspectRatio: `${width} / ${height}` }}
      data-testid={`marketing-asset-${assetKey}`}
      data-asset-key={assetKey}
      data-asset-kind={asset?.kind || "unknown"}
      data-asset-state={state}
    >
      {sources && state !== "error" && (
        <picture>
          {sources.avifSrcSet && <source type="image/avif" srcSet={sources.avifSrcSet} sizes={sizes} />}
          {sources.webpSrcSet && <source type="image/webp" srcSet={sources.webpSrcSet} sizes={sizes} />}
          <img
            key={loadKey}
            src={sources.fallbackSrc}
            alt={resolvedAlt}
            width={width}
            height={height}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            onLoad={markImageReadyAfterDecode}
            onError={() => setLoadState({ key: loadKey, value: "error" })}
            className={`absolute inset-0 h-full w-full transition-opacity duration-300 ${fit === "cover" ? "object-cover" : "object-contain"} ${state === "ready" ? "opacity-100" : "opacity-0"}`}
          />
        </picture>
      )}

      {state !== "ready" && (
        <Placeholder
          state={state}
          label={pendingLabel}
          reducedMotion={reducedMotion}
          announce={announce}
          onRetry={() => setAttempt((value) => value + 1)}
        />
      )}
    </div>
  );
}

export default MarketingAssetSlot;
